import React, { FC, useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Slider,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import Header from '../../components/Header';
import Explainer, { ExplainerSection } from '../../components/Explainer';
import TableWidget, { HeaderMappingItem } from '../../helpers/tableBuilder';
import {
  fetchPlxProducts,
  savePlxProduct,
  deletePlxProduct,
  fetchCpaPercentage,
  saveCpaPercentage,
  fetchAnfoWertParams,
  AnfoWertParams,
  PlxProduct,
} from '../../helpers/plxOrdersData';
import { tokens } from '../../theme';
import { formatNumber } from '../../helpers/numberUtils';
import LoadingSpinner from '../../components/LoadingSpinner';

// Fenster-Arten des Anfo-Wert-Modells, in Worten fuer die Tabelle.
const WINDOW_LABEL: Record<string, string> = {
  current: 'aktuell',
  historisch: 'eigener letzter Zeitraum',
  pool: 'Durchschnitt aller Produkte',
  onetime: 'Einmalfaktura',
  legacy: 'Altwert',
  zellen: 'Testphase + Sofortzahlung gemischt',
  zellenT: 'nur Testphase-Angebote',
  zellenS: 'nur Angebote mit Sofortzahlung',
  'm4-snapshot': 'Sicherung m4',
};

/** m5: Grundlage nach den Angeboten des Produkts — „gemischt" nur, wenn es beide Zahlungsarten gibt. */
const fensterArt = (kind: string | null | undefined, offers: { zahlungsart: string }[]): string => {
  if (kind !== 'zellen') return kind ?? '';
  const t = offers.some((o) => o.zahlungsart === 'T');
  const s = offers.some((o) => o.zahlungsart === 'S');
  return t && !s ? 'zellenT' : s && !t ? 'zellenS' : 'zellen';
};

/** m5: Werte je Angebot eines Produkts als kurze Zeile (Testphase / Sofortzahlung, Spanne über die Angebote). */
const offerSummary = (offers: { zahlungsart: string; anfo_wert: number }[]): string => {
  const span = (art: string) => {
    const v = offers.filter((o) => o.zahlungsart === art).map((o) => Number(o.anfo_wert));
    if (!v.length) return null;
    const lo = Math.min(...v);
    const hi = Math.max(...v);
    return lo === hi ? `${formatNumber(lo, 0)}\u00a0€` : `${formatNumber(lo, 0)}–${formatNumber(hi, 0)}\u00a0€`;
  };
  const t = span('T');
  const s = span('S');
  return [t ? `Testphase ${t}` : null, s ? `Sofortzahlung ${s}` : null].filter(Boolean).join(' · ') || '–';
};

const EXPLAINER: ExplainerSection[] = [
  {
    label: 'Datengrundlage',
    body: (
      <>
        Alle gültigen FIT-Bestellungen (ohne Stornos, Testbestellungen und unvollständige
        Bestellungen) mit ihren tatsächlich bezahlten Beträgen, brutto (inkl. MwSt.) nach
        Rückbuchungen. Gerechnet wird je Anfo, also je Bestellung eines Abo-Produkts.
      </>
    ),
  },
  {
    label: 'Berechnung (seit 13.10.2026)',
    body: (
      <>
        <strong>Bewertet wird je Bestellung, nicht mehr je Produkt.</strong> Solange ihre Zahlung
        noch nicht zählt, trägt eine Bestellung den Wert ihres Angebots: Angebote mit Zahlung vorab
        (Jahresbezug oder ohne Testphase) starten nach dem ersten Rechnungsbetrag — nicht
        proportional, weil teure Angebote seltener bezahlt werden, und gedeckelt bei 2.500 € —,
        Angebote mit Testphase beim Durchschnitt aller Testphasen-Anfos; eigene Zahlungen des
        Produkts ziehen den Wert nach. Danach (Zahlung vorab ab Tag 35, mit Testphase 45 Tage nach
        deren Ende) gilt der tatsächlich gezahlte Betrag, auf 36 Monate hochgerechnet; wer nie
        gezahlt hat, zählt dann 0 €. Die Bestellwerte werden täglich nachgezogen. Die Spalte
        „Anfo-Wert (Einzelwert)“ mischt beide Zahlungsarten nach ihrem Anteil und gilt nur, wo keine
        einzelne Bestellung bewertet wird (Prognosen, Ranglisten, Spalte „Produkte“).
      </>
    ),
  },
  {
    label: 'Bis 13.10.2026 (Modell m4)',
    body: (
      <>
        Anfo-Wert = R × G je Produkt: R war, was eine Anfo des Produkts in den ersten 75 Tagen
        durchschnittlich eingebracht hat, G die Hochrechnung auf 36 Monate; monatlich gerechnet und
        über drei Monate geglättet. Die Glättung beginnt mit m5 neu.
      </>
    ),
  },
  {
    label: 'Aussage',
    body: (
      <>
        Der Anfo-Wert ist der erwartete Umsatz einer Anfo über 36 Monate, brutto (inkl. MwSt.). Der CPA-Wert
        ist der Anteil davon, der für die Gewinnung ausgegeben werden darf (Regler oben) — ein
        Budget-Limit, keine Ist-Kosten.
      </>
    ),
  },
  {
    label: 'Wichtig',
    highlight: true,
    body: (
      <>
        Anfo-Wert und CPA-Wert kommen ausschließlich aus dem Modelllauf und lassen sich hier nicht
        von Hand setzen. Die Wandlungsraten pflegt ein wöchentlicher Abgleich; sie dienen nur der
        Cashflow-Prognose.
      </>
    ),
  },
];

const EMPTY_FORM = {
  plx_product: '',
  name: '',
  is_one_time: false,
  description: '',
  default_quarterly_price: 0,
};

const PlxProductsPage: FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [cpaPercentage, setCpaPercentage] = useState<number | null>(null); // Saved value from DB
  const [sliderValue, setSliderValue] = useState(40); // Visual slider value
  const [cpaConfirmDialogOpen, setCpaConfirmDialogOpen] = useState(false);
  const [cpaError, setCpaError] = useState<string | null>(null);
  const [tableKey, setTableKey] = useState(0);
  const [modelState, setModelState] = useState<AnfoWertParams | null>(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    let cancelled = false;
    fetchCpaPercentage()
      .then((p) => {
        if (cancelled) return;
        setCpaPercentage(p);
        setSliderValue(p);
      })
      .catch((error) => !cancelled && setCpaError(String(error?.message ?? error)));
    fetchAnfoWertParams()
      .then((s) => !cancelled && setModelState(s))
      .catch((error) => console.error('Error loading Anfo-Wert state:', error));
    return () => {
      cancelled = true;
    };
  }, []);

  // Called during slider drag - updates visual only
  const handleSliderChange = useCallback((_event: Event, value: number | number[]) => {
    setSliderValue(value as number);
  }, []);

  // Called when slider released - show confirmation if value changed
  const handleSliderCommit = useCallback(
    (_event: Event | React.SyntheticEvent, value: number | number[]) => {
      if ((value as number) !== cpaPercentage) setCpaConfirmDialogOpen(true);
    },
    [cpaPercentage],
  );

  const handleCpaConfirm = useCallback(async () => {
    try {
      await saveCpaPercentage(sliderValue);
      setCpaPercentage(sliderValue);
      setCpaError(null);
      setTableKey((prev) => prev + 1);
    } catch (error: any) {
      setCpaError(`CPA-Anteil nicht gespeichert: ${error?.message ?? error}`);
      if (cpaPercentage !== null) setSliderValue(cpaPercentage);
    } finally {
      setCpaConfirmDialogOpen(false);
    }
  }, [sliderValue, cpaPercentage]);

  const handleCpaCancel = useCallback(() => {
    setCpaConfirmDialogOpen(false);
    if (cpaPercentage !== null) setSliderValue(cpaPercentage);
  }, [cpaPercentage]);

  const headerMapping: Record<string, HeaderMappingItem> = {
    plx_product: { name: 'Produkt-Code', type: 'string' },
    name: { name: 'Produktname', type: 'string' },
    product_type: { name: 'Produkttyp', type: 'string' },
    default_quarterly_price: { name: 'Listenpreis (€)', type: 'number', digits: 2 },
    expected_conversion_rate_quarterly: {
      name: 'W.Rate Quartal (%, Prognose)',
      type: 'number',
      digits: 2,
    },
    expected_conversion_rate_upsell: {
      name: 'W.Rate Upsell (%, Prognose)',
      type: 'number',
      digits: 2,
    },
    anfo_wert: { name: modelState?.model === 'm5' ? 'Anfo-Wert (€ brutto, Einzelwert)' : 'Anfo-Wert (€ brutto)', type: 'number', digits: 2 },
    ...(modelState?.model === 'm5'
      ? { anfo_offers: { name: 'Anfo-Wert je Angebot', type: 'string' as const } }
      : {}),
    anfo_window: { name: 'Grundlage', type: 'string' },
    anfo_cpa: {
      name: `CPA-Wert (€${cpaPercentage !== null ? `, ${cpaPercentage} %` : ''})`,
      type: 'custom',
      customRender: (value: unknown) => (
        <span>
          {value === null || value === undefined
            ? 'ohne CPA'
            : `${formatNumber(Number(value), 2)} €`}
        </span>
      ),
    },
    actions: {
      name: 'Aktionen',
      type: 'custom',
      customRender: (value: any) => value,
    },
  };

  const handleOpen = () => {
    setIsEdit(false);
    setFormData(EMPTY_FORM);
    setDialogOpen(true);
  };

  const handleEdit = (product: PlxProduct) => {
    setIsEdit(true);
    setFormData({
      plx_product: product.plx_product,
      name: product.name,
      is_one_time: !!product.is_one_time,
      description: product.description || '',
      default_quarterly_price: Number(product.default_quarterly_price) || 0,
    });
    setDialogOpen(true);
  };

  const handleClose = () => setDialogOpen(false);

  const handleSave = async () => {
    if (!formData.default_quarterly_price || formData.default_quarterly_price <= 0) {
      alert(
        formData.is_one_time
          ? 'Bitte einen gültigen Einmalpreis eingeben (> 0).'
          : 'Bitte einen gültigen Listenpreis je Quartal eingeben (> 0).',
      );
      return;
    }
    setLoading(true);
    try {
      const res = await savePlxProduct(formData);
      if (res.created && res.warning) {
        alert(
          `Produkt angelegt. Anfo-Wert noch leer (${res.warning}) — er kommt mit dem nächsten Monatslauf.`,
        );
      }
      handleClose();
      window.location.reload();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Fehler beim Speichern des Produkts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (plx_product: string) => {
    if (!confirm(`Möchten Sie das Produkt "${plx_product}" wirklich löschen?`)) return;
    try {
      await deletePlxProduct(plx_product);
      window.location.reload();
    } catch (error: any) {
      console.error('Error deleting product:', error);
      alert(error.message || 'Fehler beim Löschen des Produkts');
    }
  };

  const getData = async () => {
    const products = await fetchPlxProducts();
    const byId = new Map((modelState?.products ?? []).map((h) => [h.product_id, h]));
    const offers = modelState?.offers ?? [];
    return products.map((p) => {
      const h = byId.get(p.id);
      return {
        ...p,
        anfo_offers: p.is_one_time ? '–' : offerSummary(offers.filter((o) => o.plx_product === p.plx_product)),
        product_type: p.is_one_time ? 'Einmalfaktura' : 'Abo',
        // Einmalprodukte haben keine Wandlungsrate — dort steht nur der Spalten-Default (30).
        expected_conversion_rate_quarterly: p.is_one_time ? undefined : p.expected_conversion_rate_quarterly,
        expected_conversion_rate_upsell: p.is_one_time ? undefined : p.expected_conversion_rate_upsell,
        anfo_window: h
          ? `${WINDOW_LABEL[fensterArt(h.window_kind, offers.filter((o) => o.plx_product === p.plx_product))] ?? '–'}${h.n ? ` (n = ${h.n})` : ''}`
          : '–',
        // Einmalprodukte ohne Preis/Wert: „–" statt „0,00".
        anfo_wert: p.anfo_wert ?? undefined,
        default_quarterly_price: p.default_quarterly_price ?? undefined,
        // Kein stilles Auffuellen: fehlt der CPA-Wert, zeigt die Tabelle „ohne CPA".
        anfo_cpa: p.anfo_cpa ?? undefined,
        actions: (
          <Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(p);
              }}
              color="primary"
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(p.plx_product);
              }}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ),
      };
    });
  };

  const run = modelState?.run;
  const params = modelState?.params;
  const stand = !modelState
    ? null
    : !run
      ? 'Noch kein Modelllauf — Anfo-Werte stammen aus dem alten Verfahren.'
      : run.mode === 'restore-legacy'
        ? `Altwerte seit ${run.as_of?.slice(0, 10)} zurückgeholt — Modell pausiert.`
        : run.mode === 'restore-m4'
          ? `Seit ${run.as_of?.slice(0, 10)} wieder Modell m4 (Wert je Produkt) — der nächste Tageslauf rechnet neu.`
          : modelState?.model === 'm5'
            ? `Modell m5 · Stand ${run.as_of?.slice(0, 10)} (Lauf #${run.id}, Monat ${run.calc_month})` +
              (params?.m5?.gCell
                ? ` · Hochrechnung Testphase ${formatNumber(params.m5.gCell.T, 3)}, Sofortzahlung ${formatNumber(params.m5.gCell.S, 3)}`
                : '') +
              ` · ${(modelState.offers ?? []).length} Angebote bewertet` +
              ' · Bestellungen tragen den Wert ihres Angebots bzw. ihrer Zahlung; der Einzelwert gilt für Stellen ohne Bestellbezug.'
            : `Stand ${run.as_of?.slice(0, 10)} (Lauf #${run.id}, Monat ${run.calc_month})` +
              (params?.g ? ` · Hochrechnung G ${formatNumber(params.g, 3)}` : '') +
              (params?.mu ? ` · Ø R ${formatNumber(params.mu, 2)} €` : '') +
              (params?.priorRuns ? ` · geglättet über ${params.priorRuns.length + 1} Monat(e)` : '');

  return (
    <Box m="20px">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <Header title="PLX Produkte" subtitle="Produkte für PLX Bestellungen verwalten" />
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 280 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              CPA-Anteil:
            </Typography>
            <Slider
              value={sliderValue}
              onChange={handleSliderChange}
              onChangeCommitted={handleSliderCommit}
              min={10}
              max={100}
              step={5}
              disabled={cpaPercentage === null}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v}%`}
              sx={{ width: 120 }}
            />
            <Typography variant="body2" sx={{ minWidth: 40, fontWeight: 'bold' }}>
              {sliderValue}%
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={handleOpen}
            sx={{ height: '40px' }}
          >
            Neues Produkt
          </Button>
        </Box>
      </Box>

      {cpaError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {cpaError}
        </Alert>
      )}

      <Card sx={{ mb: 2, backgroundColor: colors.primary[400] }}>
        <CardContent>
          <Typography variant="h5" color={colors.greenAccent[500]} mb={1}>
            Anfo-Wert-Modell
          </Typography>
          <Typography variant="body2">{stand ?? 'Stand wird geladen …'}</Typography>
          <Typography variant="body2" mt={1} color="warning.main">
            Produkte lassen sich nur löschen, solange keine Werbeaktion auf sie zeigt.
          </Typography>
        </CardContent>
      </Card>

      <Box mb={3}>
        <Explainer title="Wie entsteht der Anfo-Wert?" sections={EXPLAINER} />
      </Box>

      <Box
        sx={{
          backgroundColor: colors.primary[400],
          borderRadius: '4px',
          overflow: 'hidden',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <TableWidget<PlxProduct & { actions: React.ReactElement }>
          key={`${tableKey}-${modelState ? 'm' : 'x'}`}
          getData={getData}
          detailsHeaderMapping={headerMapping}
          initialSortColumn="name"
          searchByColumn="name"
          excelDownloadName="plx-products"
        />
      </Box>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isEdit ? 'Produkt bearbeiten' : 'Neues Produkt erstellen'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Produkt-Code"
                value={formData.plx_product}
                onChange={(e) =>
                  setFormData({ ...formData, plx_product: e.target.value.toUpperCase() })
                }
                required
                disabled={isEdit}
                helperText={
                  isEdit ? 'Produkt-Code kann nicht geändert werden' : 'z.B. FMM, TST, TAM'
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Produktname"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                helperText="z.B. Hot Stock Insight, Top Stock Trader"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Beschreibung"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                helperText="Optionale Beschreibung des Produkts"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_one_time}
                    onChange={(e) => setFormData({ ...formData, is_one_time: e.target.checked })}
                  />
                }
                label="Einmalfaktura (einmalige Zahlung ohne Abo)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={formData.is_one_time ? 'Einmalpreis (€)' : 'Listenpreis je Quartal (€)'}
                type="number"
                value={formData.default_quarterly_price}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    default_quarterly_price: parseFloat(e.target.value) || 0,
                  })
                }
                inputProps={{ min: 0, step: 0.01 }}
                required
                helperText={
                  formData.is_one_time
                    ? 'Einmaliger Kaufpreis — Grundlage des Anfo-Werts, solange es noch keine Bestellungen gibt'
                    : 'z.B. 249,75 — dient der Erkennung von Vorkasse-Zahlern'
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Anfo-Wert und CPA-Wert rechnet das Modell. Ein neues Produkt bekommt sofort einen
                Startwert (Durchschnitt aller Produkte bzw. Einmal-Regel), ab dem nächsten
                Monatslauf seinen eigenen.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Abbrechen</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={loading || !formData.plx_product || !formData.name}
          >
            {loading ? <LoadingSpinner /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cpaConfirmDialogOpen} onClose={handleCpaCancel} maxWidth="sm" fullWidth>
        <DialogTitle>CPA-Anteil ändern</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            CPA-Anteil von <strong>{cpaPercentage}%</strong> auf <strong>{sliderValue}%</strong>{' '}
            ändern?
          </Typography>
          <Typography variant="body2" color="warning.main">
            Der CPA-Wert aller Produkte wird sofort neu berechnet (Anfo-Wert × Anteil). Nur für
            Admins.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCpaCancel} color="inherit">
            Abbrechen
          </Button>
          <Button onClick={handleCpaConfirm} variant="contained" color="primary">
            Bestätigen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlxProductsPage;
